import { FC, MutableRefObject, useRef, useState } from 'react';

import { useOnClickOutside } from '@common/hooks';
import { Box, IconButton } from '@base-app';
import { DropdownItem, DropdownProps, DropdownVariant } from './Dropdown.types';
import {
  DropdownButtonStyled,
  DropdownContainerStyled,
  DropdownIconRotatedStyled,
  DropdownIconStyled,
  DropdownListStyled,
  DropdownTableIconRotatedStyled,
  DropdownTableIconStyled,
} from './Dropdown.styles';
import { Variants } from '@common/types';
import { useMenuPosition } from '@components/hooks';

import DropdownListItem from './DropdownListItem';

const Dropdown: FC<DropdownProps> = ({
  css,
  disabled,
  buttonStyles = {},
  icon,
  id,
  items,
  onSelect,
  selectable,
  size,
  tableVariant,
  title,
  variant = DropdownVariant.DEFAULT,
}) => {
  const ref = useRef(null);
  const [isComponentVisible, setIsComponentVisible] = useState(false);
  const [itemSelected, setItemSelected] = useState<number>();
  const dropdownRef = useRef() as MutableRefObject<HTMLButtonElement>;
  const dropdownListRef = useRef() as MutableRefObject<HTMLUListElement>;

  if (variant !== DropdownVariant.PILL && icon === undefined && title === undefined) {
    throw new Error('An icon or title must be provided.');
  }

  const handleClickOutsideMenu = (): void => {
    setIsComponentVisible(false);
  };

  useOnClickOutside(ref, handleClickOutsideMenu);

  const handleClickBtn = (): void => {
    setIsComponentVisible((prevState: boolean): boolean => !prevState);
  };

  const handleClickItem = (_: DropdownItem, index: number): void => {
    onSelect?.(items[index], index);

    if (selectable === true) {
      setItemSelected(index);
    } else {
      setIsComponentVisible(false);
    }
  };

  useMenuPosition({
    isDropdownOpen: isComponentVisible,
    dropdownRef,
    listRef: dropdownListRef,
  });

  const isItemSelected = (index: number): boolean => itemSelected === index;

  const dropdownButton =
    icon != null && title == null ? (
      <IconButton
        data-testid={id ? `dropdown-button-${id}` : 'dropdown-button'}
        disabled={disabled}
        ref={dropdownRef}
        onClick={handleClickBtn}
        variant={Variants.SECONDARY}
      >
        {icon}
      </IconButton>
    ) : (
      <DropdownButtonStyled
        css={{
          color: '$textPrimary',
          ...buttonStyles,
        }}
        data-testid={id ? `dropdown-button-title-${id}` : 'dropdown-button-title'}
        ref={dropdownRef}
        disabled={disabled}
        onClick={handleClickBtn}
        size={size}
        variant={variant}
      >
        <>
          <Box>{title}</Box>
          {tableVariant != null && !tableVariant ? (
            isComponentVisible ? (
              <DropdownIconRotatedStyled variant={variant} size={size} />
            ) : (
              <DropdownIconStyled variant={variant} size={size} />
            )
          ) : isComponentVisible ? (
            <DropdownTableIconRotatedStyled />
          ) : (
            <DropdownTableIconStyled />
          )}
        </>
      </DropdownButtonStyled>
    );

  return (
    <DropdownContainerStyled ref={ref} css={{ css }}>
      {dropdownButton}
      {isComponentVisible && (
        <DropdownListStyled ref={dropdownListRef} variant={variant} size={size}>
          {items?.map((item, index) => (
            <DropdownListItem
              isSelected={title === item.label}
              item={item}
              key={index}
              onClick={() => handleClickItem(item, index)}
              selectableMode={selectable}
              selected={isItemSelected(index)}
            />
          ))}
        </DropdownListStyled>
      )}
    </DropdownContainerStyled>
  );
};

Dropdown.defaultProps = {
  disabled: false,
  selectable: false,
  tableVariant: false,
};

export default Dropdown;
